import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Card } from '@televet/kibble-ui/Card';
import { Flex, VStack, Box } from '@televet/kibble-ui/chakra';

import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/llm-models/add')({
  component: AddLlmModel,
  beforeLoad: () => {
    return {
      pageTitle: 'Add LLM Model',
    };
  },
});

export interface LlmModelFormData {
  name: string;
  externalModelId: string;
  modelProvider?: string;
}

export function LlmModelForm({
  onSubmit,
  onCancel,
  errors,
  register,
  handleSubmit,
  title,
}: {
  onSubmit: (data: LlmModelFormData) => void;
  onCancel: () => void;
  errors: any;
  register: any;
  handleSubmit: any;
  title: string;
}) {
  return (
    <Flex
      className="LlmModelForm__Container"
      flex="1 1 auto"
      direction="column"
      align="stretch"
      h="100%"
    >
      <Flex flex="1 1 auto" justify="center" align="flex-start" pt={8}>
        <Card
          flex="1 1 auto"
          direction="column"
          borderColor="border.default"
          maxW="3xl"
        >
          <Box p={10}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={6} align="stretch">
                <TextInput
                  label="Name"
                  required
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                  placeholder="Enter model name"
                  autoFocus
                  {...register('name', { required: 'Name is required' })}
                />

                <TextInput
                  label="External Model ID"
                  required
                  invalid={!!errors.externalModelId}
                  errorText={errors.externalModelId?.message}
                  placeholder="Enter external model ID (e.g., us.anthropic.claude-sonnet-5)"
                  {...register('externalModelId', {
                    required: 'External model ID is required',
                  })}
                />

                <Flex justify="flex-end">
                  <Flex gap={2}>
                    <Button variant="secondary" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button type="submit">Save</Button>
                  </Flex>
                </Flex>
              </VStack>
            </form>
          </Box>
        </Card>
      </Flex>
    </Flex>
  );
}

function AddLlmModel() {
  const navigate = useNavigate();

  const { mutate } = useMutation(
    trpc.llmModel.createOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'LLM model created successfully',
          description: data.message,
        });
        navigate({ to: '/llm-models' });
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to create LLM model';
        toaster.error({
          title: 'Failed to create LLM model',
          description: errorMessage,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LlmModelFormData>();

  const onSubmit = (data: LlmModelFormData) => {
    mutate({
      name: data.name,
      externalModelId: data.externalModelId,
      modelProvider: 'AwsBedrock',
    });
  };

  const onCancel = () => {
    navigate({ to: '/llm-models' });
  };

  return (
    <LlmModelForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
      title="Add LLM Model"
    />
  );
}
